<template>
  <div
    class="group relative border-b border-gray-100 px-4 py-2.5 transition-colors hover:bg-gray-50"
  >
    <div class="flex w-full flex-row justify-start">
      <div class="shrink-0">
        <Avatar
          v-if="annotation.channel === AnnotationChannel.USER"
          :user="createdByUser"
          size="size-7"
        />
        <Icon
          v-else
          :name="cardDetails.iconName"
          class="h-6 w-7"
          :class="cardDetails.iconColor"
        />
      </div>
      <div class="ml-1 flex w-full flex-col">
        <div
          v-if="annotation.channel === AnnotationChannel.USER"
          class="text-xs leading-none font-medium text-black"
        >
          {{ annotation.user?.name ?? "An unknown user" }}
        </div>
        <div v-else></div>
        <div
          class="text-xs leading-tight font-medium tracking-tight text-gray-700"
        >
          {{ dateFormat(annotation.start) }} {{ durationText.text }}
          <span
            v-if="durationText.label"
            class="ml-1 rounded-full px-2 py-px text-xs font-semibold"
            :class="{
              'bg-blue-200 text-blue-700': durationText.label === 'Ongoing',
              'bg-green-200 text-green-700': durationText.label === 'Finished',
            }"
            >{{ durationText.label }}
          </span>
        </div>
        <div class="text-xs font-medium text-gray-600">
          {{ annotation.type }}
        </div>
      </div>
    </div>
    <div class="mx-6 flex px-2 py-1 text-xs leading-5 font-medium">
      <div>
        <template v-if="annotation.channel !== AnnotationChannel.USER">
          <AutomatedAnnotationDescription :annotation="annotation" />
        </template>
        <template v-else>
          <span class="break-words">
            <div v-if="annotation.feedItem">
              {{ lodash.truncate(annotation.description, { length: 50 }) }}
            </div>
            <RouterLink
              v-if="annotation.feedItem"
              class="inline-link font-semibold"
              :to="{ name: 'feed-post', params: { id: annotation.feedItem } }"
              >Go to post
            </RouterLink>
            <div v-else>
              {{
                !descriptionIsExpanded
                  ? lodash.truncate(annotation["description"], { length: 75 })
                  : annotation["description"]
              }}
            </div>
          </span>
          <button
            v-if="descriptionIsTruncated && !annotation.feedItem"
            class="cursor-pointer rounded-md font-semibold text-blue-600 hover:text-blue-700 hover:underline focus:underline focus:outline-none"
            :class="descriptionIsExpanded ? 'block' : 'ml-1 inline'"
            @click="toggleDescriptionExpansion()"
          >
            {{ !descriptionIsExpanded ? "Expand" : "Minimize" }}
          </button>
        </template>
      </div>
    </div>
    <div class="sr-only group-hover:not-sr-only focus-within:not-sr-only">
      <button
        class="absolute right-0 bottom-0 m-1 rounded-full p-1 text-red-600 transition duration-300 hover:bg-red-200 focus:bg-red-200 focus:outline-none"
        aria-label="Delete annotation"
        :disabled="isDeleting"
        @click="deleteAnnotation"
      >
        <Icon name="trash-alt" class="size-6 p-1" />
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { PropType, computed, ref } from "vue";
import {
  AnnotationChannel,
  EventDuration,
  Annotation,
  annotationChannelDetail,
  useDeleteUserAnnotationMutation,
  useDeleteAutomatedAnnotationMutation,
} from "@/services/annotation";
import Icon from "@/components/Core/Icon.vue";
import { USER_EVENT_TYPE_ICON } from "@/services/icons";
import { SystemUser } from "@/services/user";
import * as lodash from "lodash-es";
import Avatar from "@/components/Core/Avatar.vue";
import AutomatedAnnotationDescription from "@/components/Core/Annotations/AutomatedAnnotationDescriptions/AutomatedAnnotationDescription.vue";
import { DateTime } from "luxon";
import { useManageAnnotationsTracking } from "@/services/gtm";

const props = defineProps({
  annotation: {
    type: Object as PropType<Annotation>,
    required: true,
  },
});

const descriptionIsExpanded = ref(false);
const createdByUser = computed(() => {
  if (props.annotation.channel !== AnnotationChannel.USER) return SystemUser;
  return props.annotation.user;
});
const descriptionIsTruncated = computed(() => {
  // is UserAnnotation
  if ("description" in props.annotation) {
    return props.annotation.description.length > 75;
  }
  return false;
});
function toggleDescriptionExpansion(): void {
  descriptionIsExpanded.value = !descriptionIsExpanded.value;
}
function dateFormat(date: string): string {
  return DateTime.fromISO(date).toLocaleString(DateTime.DATE_MED);
}
const durationText = computed(() => {
  const dur = props.annotation.duration;
  const end = props.annotation.end;
  let endingText = "";
  let labelText = "";
  if (end !== null && dur !== EventDuration.SINGLE_DAY) {
    endingText = `- ${dateFormat(end)}`;
    labelText =
      DateTime.fromISO(end) >= DateTime.local() ? "Ongoing" : "Finished";
  }
  if (dur === EventDuration.CONTINUOUS) {
    labelText = "Ongoing";
  }

  return { text: endingText, label: labelText };
});

const cardDetails = computed(() => {
  if (props.annotation.channel === AnnotationChannel.USER) {
    return {
      iconName: USER_EVENT_TYPE_ICON[props.annotation.type],
      iconColor: "text-fuchsia-700",
    };
  }
  return annotationChannelDetail(props.annotation.channel);
});

const gtm = useManageAnnotationsTracking();

const deleteUserAnnotationMutation = useDeleteUserAnnotationMutation();
const deleteAutomatedAnnotationMutation =
  useDeleteAutomatedAnnotationMutation();

const isDeleting = computed(() =>
  props.annotation.channel === AnnotationChannel.USER
    ? deleteUserAnnotationMutation.isPending
    : deleteAutomatedAnnotationMutation.isPending,
);

function deleteAnnotation(): void {
  if (props.annotation.channel === AnnotationChannel.USER) {
    gtm.saveDelete(`User generated - ${props.annotation.type}`);
    deleteUserAnnotationMutation.mutate(props.annotation.pk);
  } else {
    gtm.saveDelete(`System generated - ${props.annotation.type}`);
    deleteAutomatedAnnotationMutation.mutate(props.annotation.pk);
  }
}
</script>
